import PostHog from 'analytics/posthog';
import CheckoutAnalytics from 'analytics/checkout';
import { setRollbarPerson } from 'analytics/rollbar';

import FacebookPixel from './scripts/facebookPixel';
import Gtag from './scripts/gtag';
import ItercomScript from './scripts/intercom';
import OmnisendScript from './scripts/omnisend';
import LogDNA from './scripts/logdna';
import SETTINGS from '../settings';

let timeout;

const tracker = {
  /**
   * /pages/_app.js
   */
  onAppInit: () => {
    // Unconmment to log data
    // console.log('TRACKER', 'onAppInit');

    LogDNA.init();
    PostHog.init(SETTINGS.SCRIPTS.POSTHOG_KEY, SETTINGS.SCRIPTS.POSTHOG_HOST);
  },

  /**
   * /pages/_app.js
   */
  pageView: (route, extraProps = {}) => {
    setTimeout(() => {
      const pageLocation = window.location.href;
      const pagePath = window.location.pathname;
      const pageTitle = window.document.title;

      // Unconmment to log data
      // console.log('TRACKER', 'pageView', { route, pageLocation, pagePath, pageTitle, extraProps });

      FacebookPixel.pageView();
      Gtag.pageView(pageLocation, pagePath, pageTitle);
      OmnisendScript.pageView();
      PostHog.pageView(extraProps);
    }, 200);
  },

  productView: skuProduct => {
    if (!skuProduct) {
      return;
    }

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      FacebookPixel.productView(skuProduct.id, skuProduct.name, skuProduct.price, skuProduct.currency);
    }, 200);
  },

  /**
   * /pages/_app.js
   */
  cartItemAdd: skuProduct => {
    // Unconmment to log data
    // console.log('TRACKER', 'cartItemAdd', skuProduct);

    FacebookPixel.cartItemAdd(skuProduct.id, skuProduct.name, skuProduct.price, skuProduct.currency);
  },

  /**
   * /pages/cart/index.js
   */
  checkoutInit: (price, currency, promoCode, items, overall = price) => {
    // Unconmment to log data
    // console.log('TRACKER', 'checkoutInit');

    FacebookPixel.checkoutInit(price, currency, items);
    Gtag.customEvent('checkout_init');
    CheckoutAnalytics.started(overall, currency);
  },

  async orderPayment(type, operation) {
    let result;
    try {
      result = await operation();
    } catch (error) {
      tracker.orderPaymentFailure(type);
      throw error;
    }

    if (result.error && !result.orderCreated) {
      tracker.orderPaymentFailure(type);
      return result;
    }

    const order = result.data;
    CheckoutAnalytics.succeeded(
      type,
      order.meta_data?.payment_info?.overall,
      order.meta_data?.payment_info?.currency,
      order.id,
    );
    Gtag.customEvent('checkout_payment');
    return result;
  },

  orderPaymentFailure: type => {
    CheckoutAnalytics.failed(type);
    Gtag.customEvent('checkout_payment_failure');
  },

  /**
   * /pages/cart/_confirmation.js
   */
  orderSend: orderData => {
    // Unconmment to log data
    // console.log('TRACKER', 'orderSend', orderData);

    const { externalOrderId, orderedItems } = orderData;
    // TODO map data in API call
    const {
      shipping_type: shippingType,
      payment_info: paymentInfo,
      shipping_address: shippingAddress,
    } = orderData.meta_data;
    const { items, overall: price, currency, applied_promo_code: promoCode, shipping } = paymentInfo;

    FacebookPixel.orderSend(price, currency, externalOrderId, items);
    Gtag.orderSend(price, currency, externalOrderId, shipping, orderedItems, promoCode);
  },

  /**
   * /pages/_app.js
   */
  userSet: (user = {}) => {
    // Unconmment to log data
    // console.log('TRACKER', 'userSet', user);
    if (typeof window !== 'undefined' && window.Rollbar) {
      setRollbarPerson(window.Rollbar, user.id, user.email);
    }
    ItercomScript.identify(user.id, user.email, user.name);
    PostHog.identify(user.id, user.email, user.name);
  },

  /**
   * /pages/_app.js - called on sign out to reset analytics identity
   */
  userClear: () => {
    PostHog.reset();
  },

  /**
   * /pages/cart/index.js
   * /pages/cart/amazon.js
   * /pages/cart/checkout.js
   */
  cartSet: (items, price, currency, email = '') => {
    // Unconmment to log data
    // console.log('TRACKER', 'cartSet', { items, price, currency, email });
  },

  /**
   * /pages/product/[productId].js
   */
  editorInit: sku => {
    // Unconmment to log data
    // console.log('TRACKER', 'editorInit', sku);
  },

  /**
   * /pages/_app.js
   */
  editorChoosePhotos: maximumNumberOfPhotos => {
    // Unconmment to log data
    // console.log('TRACKER', 'editorChoosePhotos', maximumNumberOfPhotos);
  },

  /**
   * /pages/_app.js
   */
  editorUploadPhotos: numberOfPhotos => {
    // Unconmment to log data
    // console.log('TRACKER', 'editorUploadPhotos', numberOfPhotos);
  },

  /**
   * /pages/_app.js
   */
  editorPreviewPrints: () => {
    // Unconmment to log data
    // console.log('TRACKER', 'editorPreviewPrints');
  },

  /**
   * /pages/editor/[...params].js
   */
  editorPreviewDone: (skuProduct, data) => {
    // Unconmment to log data
    // console.log('TRACKER', 'editorPreviewDone', { skuProduct, data });
  },

  /**
   * /pages/_app.js
   */
  editorUpgradeProduct: skuProduct => {
    // Unconmment to log data
    // console.log('TRACKER', 'editorUpgradeProduct', skuProduct);
  },

  /**
   * /pages/signin.js
   */
  signIn: userData => {
    // Unconmment to log data
    // console.log('TRACKER', 'signIn', userData);
    Gtag.customEvent('signin_email_success');
    PostHog.customEvent('signin_email_success');
  },

  /**
   * /pages/signin.js
   */
  signUp: userData => {
    // Unconmment to log data
    // console.log('TRACKER', 'signUp', userData);
    Gtag.customEvent('signup_email_success');
    PostHog.customEvent('signup_email_success');
  },

  /**
   * /pages/signin.js
   */
  signInWithAmazon: userData => {
    // Unconmment to log data
    // console.log('TRACKER', 'signInWithAmazon', userData);
    Gtag.customEvent('signin_amazon_success');
    PostHog.customEvent('signin_amazon_success');
  },

  /**
   * /pages/signin.js
   */
  signInWithFacebook: userData => {
    // Unconmment to log data
    // console.log('TRACKER', 'signInWithFacebook', userData);
    Gtag.customEvent('signin_facebook_success');
    PostHog.customEvent('signin_facebook_success');
  },

  /**
   * /pages/_app.js
   */
  onCustomEvent: (action, payload = {}) => {
    // Unconmment to log data
    // console.log('TRACKER', 'onCustomEvent', action, payload);

    if (action === 'editor_thumbnail_summary') {
      PostHog.customEvent(action, {
        ...payload,
        app_version: SETTINGS.VERSION,
        release: SETTINGS.SCRIPTS.COMMIT_HASH,
        environment: SETTINGS.SCRIPTS.ROLLBAR_ENV,
      }, ['hidden', 'pagehide', 'exit'].includes(payload.flush_reason)
        ? { transport: 'sendBeacon', send_instantly: true } : undefined);
      return;
    }

    Gtag.customEvent(action);
    PostHog.customEvent(action, payload);
  },

  /**
   * /pages/_app.js - mirrors the persisted cart promo code as a PostHog super
   * property so every event in the session can be broken down by coupon.
   */
  promoCodeSet: code => {
    // Unconmment to log data
    // console.log('TRACKER', 'promoCodeSet', code);

    if (code) {
      PostHog.register({ coupon_code: code });
    } else {
      PostHog.unregister('coupon_code');
    }
  },
};

export default tracker;
